import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { getCurrentPath } from "@faire/web/common/Path";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate/Button";
import { Opacity } from "@faire/web/slate/Color";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { PartnershipLogos } from "@faire/retailer/views/PartnershipIncentiveLanding/PartnershipLogos";
import { useIncentiveForLandingPages } from "@faire/retailer/views/PartnershipIncentiveLanding/Util";

const SQUARE_BACKGROUND_IMAGE =
  "https://cdn.faire.com/fastly/da7359388c0f5e0098eeb7d9ab706288f41da68f9519a2608351d5c947cf9912.jpeg";
const AMEX_BACKGROUND_IMAGE_MOBILE =
  "https://cdn.faire.com/fastly/9aabceb9d54c745f3dd78d344ad3100cd471ff1b3f317778660b7f90f4d53b47.png";
const AMEX_BACKGROUND_IMAGE_DESKTOP =
  "https://cdn.faire.com/fastly/a4080977b61e201d9fe3fa57b5337c283d1afa45aa6028f5dd29200ba9c5ec65.png";

export const IncentiveBanner: React.FC<{
  isAmex?: boolean;
  isLoggedOut?: boolean;
}> = ({ isAmex, isLoggedOut }) => {
  const isSSR = useIsSSR();
  const image: string =
    optimizeImageUrl(
      useResponsiveValue({
        mobileAndAbove: {
          url: isAmex ? AMEX_BACKGROUND_IMAGE_MOBILE : SQUARE_BACKGROUND_IMAGE,
          width: 768,
        },
        tabletAndAbove: {
          url: isAmex ? AMEX_BACKGROUND_IMAGE_DESKTOP : SQUARE_BACKGROUND_IMAGE,
          width: 1920,
        },
      }),
      isSSR
    ) ?? "";

  const incentive = useIncentiveForLandingPages();

  return (
    <BannerWrapper $backgroundImage={image} $isAmex={isAmex}>
      <ContentWrapper>
        <PartnershipLogos isAmex={!!isAmex} />
        <Typography
          variant="displayMSerifRegular"
          as="h1"
          color={Core.text.inverse}
        >
          {isAmex ? (
            <StrictLocalMsg
              defaultMessage="Get 50% off your first order as an American Express merchant"
              description={{
                text: "Header for the Amex landing page",
                img: "https://cdn.faire.com/fastly/1c709d5495d9254c34002b870f42480ace5e109ab323c161f0289e31e81682af.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Get 50% off your first order"
              description={{
                text: "Header for the Square landing page",
                img: "https://cdn.faire.com/fastly/92b0b1ef20e8813868fea8dd1b3649ed5b07e48c317233ed9282f9d2140a451b.png",
              }}
            />
          )}
        </Typography>
        <Typography
          variant={{
            mobileAndAbove: "paragraphSansRegular",
            tabletAndAbove: "subheadingSansRegular",
          }}
          color={Core.text.inverse}
        >
          {isAmex ? (
            <StrictLocalMsg
              defaultMessage="Find and order unique products for your store and get exclusive benefits when you shop wholesale with Faire."
              description={{
                text: "Subtitle for the Amex landing page",
                img: "https://cdn.faire.com/fastly/1c709d5495d9254c34002b870f42480ace5e109ab323c161f0289e31e81682af.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Find unique products for your store, seamlessly sync your POS, and get exclusive benefits when you shop wholesale with Faire."
              description={{
                text: "Subtitle for the Square landing page",
                img: "https://cdn.faire.com/fastly/92b0b1ef20e8813868fea8dd1b3649ed5b07e48c317233ed9282f9d2140a451b.png",
              }}
            />
          )}
        </Typography>
        {isLoggedOut ? (
          <Button
            variant="secondary"
            linkTo={getCurrentPath()
              .replace({
                signUp: "1",
              })
              .toString()}
          >
            <StrictLocalMsg
              defaultMessage="Sign up to shop"
              description={{
                text: "Text for sign up button",
              }}
            />
          </Button>
        ) : null}
        <Typography variant="labelSansRegular" color={Core.text.inverse}>
          {isAmex ? (
            <StrictLocalMsg
              defaultMessage="Maximum discount is {formattedIncentiveMoney}. Offer ends 7 days after signing up for a new Faire account via this landing page. Does not apply to shipping or taxes. Offer expires 12/31/2024. Other terms apply*"
              description={{
                text: "Label with details for the Amex landing page",
                img: "https://cdn.faire.com/fastly/99cc1fcdb23dd7c8005bf4744e2b1cdb72e8f4d7a6606bd014a21db37fb24892.png",
              }}
              values={{ formattedIncentiveMoney: incentive }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Maximum discount is {formattedIncentiveMoney}. Offer ends 7 days after signing up. Other terms apply*"
              description={{
                text: "Label with details for the Amex landing page",
                img: "https://cdn.faire.com/fastly/1c709d5495d9254c34002b870f42480ace5e109ab323c161f0289e31e81682af.png",
              }}
              values={{ formattedIncentiveMoney: incentive }}
            />
          )}
        </Typography>
      </ContentWrapper>
    </BannerWrapper>
  );
};

const ContentWrapper = styled(Column).attrs({
  align: "flex-start",
  gap: "3x",
})`
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("6x", "3x")};
  }

  ${responsiveMediaQueries.tablet} {
    padding: 96px 150px 96px 0;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 900px;
    padding: 96px 340px 96px 0;
  }
`;

const BannerWrapper = styled(Column).attrs({
  align: "center",
})<{
  $backgroundImage: string;
  $isAmex: boolean | undefined;
}>`
  background-image: url("${({ $backgroundImage }) => $backgroundImage}");
  background-color: ${({ $isAmex }) =>
    Core.surface.default + ($isAmex ? Opacity.O_45 : Opacity.O_55)};
  background-blend-mode: overlay;
  background-size: cover;
  background-position: center;
  width: 100vw;
  height: fit-content;
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "5x")};
  }
`;
